import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  inject,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import type {
  Form,
  FormButton,
  FormField,
} from './interfaces/form.interface';
import type { ReactiveFormDefinition } from './interfaces/reactive-form-definition.interface';
import { InputType, type DropdownData, type Value } from './interfaces/input.interface';
import {
  ReactiveFormChange,
  ReactiveFormLifecycleEvent,
  ReactiveFormRawValueChange,
  ReactiveFormStableValueChange,
  ReactiveFormValue,
} from './interfaces/reactive-form-change.interface';
import { ReactiveDropdownCacheService } from './utils/reactive-dropdown-cache.service';
import { ReactiveOptionStoreService, type ReactiveOptionStoreEntry } from './utils/reactive-option-store.service';
import { ReactiveFieldRuntimeState } from './interfaces/reactive-field-runtime-state.interface';
import type { ReactiveChangeOrigin } from './utils/change-origin/reactive-change-origin';
import { LegacyFormDefinitionAdapter } from './utils/definition/legacy-form-definition.adapter';
import { ReactiveFormFacade } from './utils/facade/reactive-form.facade';
import { ReactiveFormEventFacade } from './utils/facade/reactive-form-event.facade';
import { ReactiveFormPatchFacade } from './utils/facade/reactive-form-patch.facade';
import { ReactiveFormDropdownFacade } from './utils/facade/reactive-form-dropdown.facade';
import { ReactiveFormMediaFacade } from './utils/facade/reactive-form-media.facade';
import { ReactiveFormNavigationFacade } from './utils/facade/reactive-form-navigation.facade';
import { ReactiveFormViewStateFacade } from './utils/facade/reactive-form-view-state.facade';
import { ReactiveFormModelFacade } from './utils/facade/reactive-form-model.facade';
import {
  RepeatableReadinessService,
  type RepeatablePendingDropdownDiagnostic,
  type RepeatableReadinessState,
  type RepeatableReadinessSummary,
} from './field/repeatable-group/logic/repeatable-readiness.service';
import {
  REACTIVE_FORM_BUILDER_DIAGNOSTICS,
  REACTIVE_FORM_BUILDER_ENVIRONMENT,
  REACTIVE_FORM_BUILDER_FILE_UPLOAD,
  REACTIVE_FORM_BUILDER_STORAGE,
  REACTIVE_FORM_BUILDER_TOUR,
  REACTIVE_FORM_NOOP_REQUEST_HANDLER,
  executeReactiveFormRequestAdapter,
  createReactiveFormRequestAdapterState,
  type ReactiveFormRequestAdapter,
  type ReactiveFormRequestHandler,
} from './adapters/reactive-form-builder.adapters';

const DEFAULT_FIELD_READY_TIMEOUT_MS = 5000;
const MAX_DROPDOWN_REFRESH_SIGNATURE_DEPTH = 4;
const MAX_DROPDOWN_REFRESH_SIGNATURE_OPTIONS = 40;

@Component({
  selector: 'app-reactive-form-builder',
  standalone: false,
  templateUrl: './form-builder.component.html',
  styleUrls: ['./form-builder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ReactiveOptionStoreService, RepeatableReadinessService],
})
export class ReactiveFormBuilderComponent<T> implements OnInit, OnDestroy, OnChanges {

  @Input() form!: Form<T>;
  @Input() reactiveDefinition: ReactiveFormDefinition<T> | null = null;
  @Input() id = '';
  @Input() previousData: T | null = null;
  @Input() rehydrateOnPreviousDataChange = false;
  @Input() serverUrl = '';

  // IMPORTANT: keep as any to avoid template variance errors (RequestHandler<T[]> -> RequestHandler<unknown> is NOT assignable)
  @Input() request: ReactiveFormRequestHandler<any> = REACTIVE_FORM_NOOP_REQUEST_HANDLER;
  @Input() getRequest: ReactiveFormRequestHandler<any> = REACTIVE_FORM_NOOP_REQUEST_HANDLER;
  @Input() requestAdapter: ReactiveFormRequestAdapter<any, any> | null = null;
  @Input() getRequestAdapter: ReactiveFormRequestAdapter<unknown, unknown> | null = null;

  @Input() undoEnabled = false;
  @Input() recordHasAssociatedData = false;
  @Input() areTipsEnabled = false;
  /**
   * Progressive dropdown loading trigger.
   * Parent should preferably pass ReplaySubject(1).
   */
  @Input() dropdownLoadTrigger: Observable<void> | null = null;


  @Input() showActionsButtons = true;

  @Input() tourAnchorsEnabled = true;

  @Input() debug = false;

  @Output() formChanges: EventEmitter<ReactiveFormChange> = new EventEmitter<ReactiveFormChange>();
  @Output() rawValueChanges: EventEmitter<ReactiveFormRawValueChange<T>> =
    new EventEmitter<ReactiveFormRawValueChange<T>>();
  @Output() stableValueChanges: EventEmitter<ReactiveFormStableValueChange<T>> =
    new EventEmitter<ReactiveFormStableValueChange<T>>();
  @Output() internalLifecycleEvents: EventEmitter<ReactiveFormLifecycleEvent<T>> =
    new EventEmitter<ReactiveFormLifecycleEvent<T>>();

  @Output() goBack: EventEmitter<T> = new EventEmitter<T>();
  @Output() afterSend: EventEmitter<T> = new EventEmitter<T>();
  @Output() hideDialog: EventEmitter<T> = new EventEmitter<T>();
  @Output() lazyDropdownRequested: EventEmitter<string> = new EventEmitter<string>();
  @Output() lazyDropdownFilterRequested: EventEmitter<{ readonly fieldName: string; readonly filter: string; }> =
    new EventEmitter<{ readonly fieldName: string; readonly filter: string; }>();

  public appName = '';
  public filterInputParcelas = false;
  public INPUT = InputType;
  public readonly resolveDropdownRawValueForSubmit = (fieldName: string, value: unknown): unknown | null =>
    this.resolveDropdownRawValue(fieldName, value);
  public readonly resolveDropdownBackendValueForSubmit = (fieldName: string, value: unknown): unknown | null =>
    this.resolveDropdownBackendValue(fieldName, value);

  public model!: FormGroup;
  public originalModel: Record<string, unknown> = {};

  private readonly destroy$ = new Subject<void>();
  private readonly builderEnvironment = inject(REACTIVE_FORM_BUILDER_ENVIRONMENT);
  private readonly diagnostics = inject(REACTIVE_FORM_BUILDER_DIAGNOSTICS);
  private readonly dropdownCache = inject(ReactiveDropdownCacheService);
  private readonly optionStore = inject(ReactiveOptionStoreService);
  private readonly repeatableReadiness = inject(RepeatableReadinessService);
  private readonly fileUploadService = inject(REACTIVE_FORM_BUILDER_FILE_UPLOAD);
  private readonly farmerTourService = inject(REACTIVE_FORM_BUILDER_TOUR);
  private readonly storage = inject(REACTIVE_FORM_BUILDER_STORAGE);
  private readonly formDropdownFacade: ReactiveFormDropdownFacade<T>;
  private readonly formFacade: ReactiveFormFacade<T>;
  private readonly formEventFacade: ReactiveFormEventFacade<T>;
  private readonly formPatchFacade: ReactiveFormPatchFacade<T>;
  private readonly formMediaFacade: ReactiveFormMediaFacade<T>;
  private readonly formNavigationFacade: ReactiveFormNavigationFacade<T>;
  private readonly formViewStateFacade: ReactiveFormViewStateFacade<T>;
  private readonly formModelFacade: ReactiveFormModelFacade<T>;

  private previousDataReady = false;
  private needsDropdowns = false;
  private isBootstrapped = false;
  private isDestroyed = false;
  private isRefreshingDropdownMappings = false;
  private isDropdownMappingsRefreshScheduled = false;
  private hasPendingDropdownMappingsRefresh = false;
  private isFinalizingExternalDropdowns = false;
  private hasPendingExternalDropdownFinalization = false;
  private lastDropdownRefreshSignature = '';
  private readonly fieldReadyWaiters = new Map<string, Array<(isReady: boolean) => void>>();
  private runtimeReactiveDefinition: ReactiveFormDefinition<T> | null = null;

  constructor(
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {
    this.appName = this.builderEnvironment.appName;
    this.filterInputParcelas = this.builderEnvironment.features?.filterInputParcelas === true;

    this.formEventFacade = new ReactiveFormEventFacade<T>({
      formChanges: this.formChanges,
      rawValueChanges: this.rawValueChanges,
      stableValueChanges: this.stableValueChanges,
      internalLifecycleEvents: this.internalLifecycleEvents,
    });
    this.formModelFacade = new ReactiveFormModelFacade<T>({
      getForm: () => this.form ?? null,
      getPreviousData: () => this.previousData,
      areExternalDropdownsReady: () => this.areExternalDropdownsReady(),
      setModel: (model) => {
        this.model = model;
      },
      setOriginalModel: (originalModel) => {
        this.originalModel = originalModel;
      },
      startDiagnostics: (operation) => this.diagnostics.start(operation, 'reactive-form-builder', this.getDiagnosticsMetadata()) ?? null,
    });
    this.formPatchFacade = new ReactiveFormPatchFacade<T>({
      getForm: () => this.form ?? null,
      getModel: () => this.model ?? null,
      markNextModelChangeOrigin: (origin) => this.formEventFacade.markNextModelChangeOrigin(origin),
      markForCheck: () => this.markForCheck(),
    });
    this.formMediaFacade = new ReactiveFormMediaFacade<T>({
      getForm: () => this.form ?? null,
      getModel: () => this.model ?? null,
      fileUploadService: this.fileUploadService,
      markForCheck: () => this.markForCheck(),
    });
    this.formNavigationFacade = new ReactiveFormNavigationFacade<T>({
      getForm: () => this.form ?? null,
      router: this.router,
      emitGoBack: () => this.goBack.emit(),
      emitHideDialog: () => this.hideDialog.emit(),
    });
    this.formDropdownFacade = new ReactiveFormDropdownFacade<T>({
      http: this.http,
      dropdownCache: this.dropdownCache,
      optionStore: this.optionStore,
      getForm: () => this.form ?? null,
      setForm: (form) => this.assignDropdownEngineForm(form),
      getModel: () => this.model ?? null,
      getPreviousData: () => this.previousData,
      getServerUrl: () => this.getServerUrl(),
      getAppName: () => this.appName,
      canMutateModelSafely: () => this.canMutateModelSafely(),
      isDestroyed: () => this.isDestroyed,
      applySelectedValue: (field, value) => this.formModelFacade.applySelected(field, value as T),
      applyResolvedFieldData: async (fieldName, origin) => this.applyDropdownResolvedFieldData(fieldName, origin),
      completeFieldDataCascade: (fieldName, origin) => this.completeDropdownDataCascade(fieldName, origin),
      publishStableValueSnapshot: (fieldName, origin) => this.publishStableModelSnapshot(fieldName, origin),
      notifyStateChanged: () => this.handleDropdownEngineStateChange(),
      markExternalDropdownsReady: (origin) => this.formFacade.markExternalDropdownsReady(origin),
      storage: this.storage,
    });
    this.formViewStateFacade = new ReactiveFormViewStateFacade<T>({
      getForm: () => this.form ?? null,
      getModel: () => this.model ?? null,
      buildFieldRuntimeStates: () => this.formDropdownFacade.buildFieldRuntimeStates(),
      hasLoadingDropdowns: (fieldStates) => this.formDropdownFacade.hasLoadingDropdowns(fieldStates),
      areExternalDropdownsReady: () => this.areExternalDropdownsReady(),
      hasPendingPreviousData: () => !!this.id && !this.previousDataReady,
      needsLegacyDropdowns: () => this.needsDropdowns,
      isDestroyed: () => this.isDestroyed,
      areTourAnchorsEnabled: () => this.tourAnchorsEnabled,
      farmerTourService: this.farmerTourService,
      markForCheck: () => this.markForCheck(),
      detectChanges: () => this.cdr.detectChanges(),
    });
    this.formFacade = new ReactiveFormFacade<T>({
      applyReactiveDefinition: (definition) => this.applyReactiveDefinition(definition),
      transformPreviousDataForForm: (previousData) => this.transformPreviousDataForForm(previousData),
      getId: () => this.id,
      getFormType: () => this.form?.type ?? null,
      getPreviousData: () => this.previousData,
      setPreviousData: (previousData) => {
        this.previousData = previousData;
      },
      setNeedsDropdowns: (needsDropdowns) => {
        this.needsDropdowns = needsDropdowns;
      },
      isBootstrapped: () => this.isBootstrapped,
      shouldRehydrateOnPreviousDataChange: () => this.rehydrateOnPreviousDataChange,
      warnPreviousDataChangedAfterBootstrap: () => this.warnPreviousDataChangedAfterBootstrap(),
      setBootstrapped: (isBootstrapped) => {
        this.isBootstrapped = isBootstrapped;
      },
      setPreviousDataReady: (isReady) => {
        this.previousDataReady = isReady;
      },
      canMutateModelSafely: () => this.canMutateModelSafely(),
      restoreDeclarativeDropdownCache: () => this.formDropdownFacade.restoreCache(),
      initializeModel: () => this.formModelFacade.initializeModel(),
      syncFieldRuntimeStates: () => this.formViewStateFacade.syncFieldRuntimeStates(),
      indexRepeatableBindingControls: () => this.formModelFacade.indexRepeatableBindingControls(),
      subscribeToFormChanges: () => this.subscribeToFormChanges(),
      executeFieldsLogic: (fieldName, origin) => this.formModelFacade.executeFieldsLogic(fieldName, origin),
      executeBeforeRepeatableHydration: () => this.executeBeforeRepeatableHydration(),
      setOriginalModelFromCurrent: () => this.formModelFacade.setOriginalModelFromCurrent(),
      registerDropdownTrigger: (trigger) => this.formDropdownFacade.registerTrigger(
        trigger,
        this.destroy$,
        () => this.requestExternalDropdownMappingsRefresh(),
      ),
      loadInitialDeclarativeDropdowns: () => this.formDropdownFacade.loadInitial(),
      capturePendingDropdownSelectionsFromPreviousData: () => this.formDropdownFacade.capturePendingSelectionsFromPreviousData(),
      capturePendingDropdownSelectionsFromCurrentModel: () => this.formDropdownFacade.capturePendingSelectionsFromCurrentModel(),
      resolvePendingDropdownSelections: () => this.formDropdownFacade.resolvePendingSelections(),
      patchFromPreviousDataPristine: () => this.formModelFacade.patchFromPreviousDataPristine(),
      patchRepeatableBindingsFromPreviousDataPristine: () => this.formModelFacade.patchRepeatableBindingsFromPreviousDataPristine(),
      remapDropdownFieldsPristine: () => this.formModelFacade.remapDropdownFieldsPristine(),
      saveDeclarativeDropdownCache: () => this.formDropdownFacade.saveCache(),
      publishSilentModelSnapshot: (changedField, origin) => this.publishSilentModelSnapshot(changedField, origin),
      loadHydratedDeclarativeDropdownDependencies: () => this.formDropdownFacade.loadHydratedDependencies(),
      updateLoadingState: () => this.formViewStateFacade.updateLoadingState(),
      refreshRenderedFields: () => this.formViewStateFacade.refreshRenderedFields(),
      markForCheck: () => this.markForCheck(),
      fetchPreviousDataById: (id, onResolved) => this.fetchPreviousDataById(id, onResolved),
    });
  }

  public get isLoading(): boolean {
    return this.formViewStateFacade.isLoading;
  }

  public get fieldStates(): ReadonlyMap<string, ReactiveFieldRuntimeState<T>> {
    return this.formViewStateFacade.fieldStates;
  }

  public get isDevelopmentMode(): boolean {
    return this.builderEnvironment.production !== true;
  }

  public get showFieldHelpButtons(): boolean {
    return this.isDevelopmentMode && this.formViewStateFacade.showFieldHelpButtons;
  }

  public get fieldRenderVersion(): number {
    return this.formViewStateFacade.fieldRenderVersion;
  }

  public get reactiveRuntimeDefinition(): ReactiveFormDefinition<T> | null {
    return this.runtimeReactiveDefinition;
  }

  public get resolvedRecordId(): string | number | null {
    return this.resolveCurrentRecordId();
  }

  public toggleFieldHelpButtons(): void {
    if (!this.isDevelopmentMode) {
      return;
    }

    this.formViewStateFacade.toggleFieldHelpButtons();
  }

  public startCurrentFormTour(): void {
    if (!this.isDevelopmentMode) {
      return;
    }

    this.formViewStateFacade.startCurrentFormTour();
  }

  public getTourAnchorId(anchorId: string): string {
    return this.formViewStateFacade.getTourAnchorId(anchorId);
  }

  /**
   * Resolves the full entity behind a dropdown value when the control stores only a primitive id.
   */
  public resolveDropdownRawValue<TEntity = unknown>(fieldName: string, value?: unknown): TEntity | null {
    return this.formDropdownFacade.resolveRawValue<TEntity>(
      fieldName,
      this.getDropdownSourceValue(fieldName, value, arguments.length > 1),
    );
  }

  /** Resolves the canonical dropdown option for the current or provided control value. */
  public resolveDropdownOption(fieldName: string, value?: unknown): ReactiveOptionStoreEntry | null {
    return this.formDropdownFacade.resolveOption(
      fieldName,
      this.getDropdownSourceValue(fieldName, value, arguments.length > 1),
    );
  }

  /** Resolves the visual label for the current or provided dropdown control value. */
  public resolveDropdownLabel(fieldName: string, value?: unknown): string {
    return this.formDropdownFacade.resolveLabel(
      fieldName,
      this.getDropdownSourceValue(fieldName, value, arguments.length > 1),
    );
  }

  /** Resolves the legacy/backend value for dropdowns that store canonical frontend ids. */
  public resolveDropdownBackendValue(fieldName: string, value?: unknown): unknown | null {
    return this.formDropdownFacade.resolveBackendValue(
      fieldName,
      this.getDropdownSourceValue(fieldName, value, arguments.length > 1),
    );
  }


  public getFormGuidanceTips(): readonly string[] {
    return this.formViewStateFacade.getFormGuidanceTips();
  }

  public trackByGuidanceTip(index: number, tip: string): string {
    return this.formViewStateFacade.trackByGuidanceTip(index, tip);
  }

  public hasVisibleFields(): boolean {
    return this.formViewStateFacade.hasVisibleFields();
  }


  private warnPreviousDataChangedAfterBootstrap(): void {
    if (this.builderEnvironment.production === true) {
      return;
    }

    console.warn(
      '[ReactiveFormBuilder] previousData changed after bootstrap and was ignored. ' +
      'Pass [rehydrateOnPreviousDataChange]="true" only for forms that intentionally reload edit data.',
    );
  }

  private resolveCurrentRecordId(): string | number | null {
    const candidates = this.isPlatformUserForm()
      ? this.getPlatformUserRecordIdCandidates()
      : this.getStandardRecordIdCandidates();

    for (const candidate of candidates) {
      if (this.isSerializableRecordId(candidate)) {
        return typeof candidate === 'number' ? candidate : String(candidate).trim();
      }
    }

    return null;
  }

  private getStandardRecordIdCandidates(): readonly unknown[] {
    return [
      this.id,
      this.model?.get('id')?.value,
      this.resolveRecordIdFromObject(this.originalModel, 'id'),
      this.resolveRecordIdFromObject(this.previousData, 'id'),
    ];
  }

  private getPlatformUserRecordIdCandidates(): readonly unknown[] {
    return [
      this.model?.get('id_plataforma')?.value,
      this.resolveRecordIdFromObject(this.originalModel, 'id_plataforma'),
      this.resolveRecordIdFromObject(this.previousData, 'id_plataforma'),
      this.id,
      this.model?.get('id')?.value,
      this.resolveRecordIdFromObject(this.originalModel, 'id'),
      this.resolveRecordIdFromObject(this.previousData, 'id'),
    ];
  }

  private isPlatformUserForm(): boolean {
    const formName = String(this.form?.name ?? '').trim().toLowerCase();

    return formName === 'usuarios' || formName === 'usuarios-summary';
  }

  private resolveRecordIdFromObject(value: unknown, key: 'id' | 'id_plataforma'): unknown {
    if (value === null || typeof value !== 'object') {
      return null;
    }

    const record = value as Record<string, unknown>;

    return record[key] ?? null;
  }

  private isSerializableRecordId(value: unknown): value is string | number {
    return (typeof value === 'string' || typeof value === 'number') && String(value).trim() !== '';
  }

  private applyReactiveDefinition(definition: ReactiveFormDefinition<T> | null): void {
    this.runtimeReactiveDefinition = definition;

    if (!definition) {
      return;
    }

    this.form = LegacyFormDefinitionAdapter.toLegacy(definition);
  }

  private resolveRuntimeReactiveDefinition(): ReactiveFormDefinition<T> | null {
    if (this.reactiveDefinition) {
      return this.reactiveDefinition;
    }

    return this.form ? LegacyFormDefinitionAdapter.fromLegacy(this.form) : null;
  }

  private transformPreviousDataForForm(previousData: T | null): T | null {
    if (previousData === null) {
      return null;
    }

    return this.form?.previousDataTransform
      ? this.form.previousDataTransform(previousData)
      : previousData;
  }


  public ngOnInit(): void {
    void this.formFacade.bootstrap(this.resolveRuntimeReactiveDefinition(), this.dropdownLoadTrigger);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('reactiveDefinition' in changes) {
      const definition = changes['reactiveDefinition']?.currentValue as ReactiveFormDefinition<T> | null | undefined;

      this.applyReactiveDefinition(definition ?? null);
    }

    if ('form' in changes && !this.reactiveDefinition && !this.isBootstrapped) {
      this.applyReactiveDefinition(this.resolveRuntimeReactiveDefinition());
    }

    if ('dropdownLoadTrigger' in changes) {
      const trigger = changes['dropdownLoadTrigger']?.currentValue as Observable<void> | null;

      this.formFacade.handleDropdownTriggerChange(trigger);
    }

    if ('previousData' in changes) {
      const current = changes['previousData']?.currentValue as T | null | undefined;

      void this.formFacade.handlePreviousDataInputChange(current);
    }
  }

  public ngOnDestroy(): void {
    this.isDestroyed = true;
    this.isDropdownMappingsRefreshScheduled = false;
    this.hasPendingDropdownMappingsRefresh = false;
    this.formDropdownFacade.destroy();
    this.formModelFacade.destroy();
    this.repeatableReadiness.clear();
    this.resolveAllReadyWaiters(false);

    this.destroy$.next();
    this.destroy$.complete();
  }

  public hasMainTableData(): boolean {
    return this.formViewStateFacade.hasMainTableData();
  }

  public returnToMainView(): void {
    this.formNavigationFacade.returnToMainView();
  }

  public getHeaderButtons(): FormButton[] {
    return this.formNavigationFacade.getHeaderButtons();
  }

  public executeConfiguredButton(button: FormButton): void {
    this.formNavigationFacade.executeConfiguredButton(button);
  }

  public executePostSubmissionLogic(res: T): void {
    this.afterSend.emit(res);
  }

  public closeDialog(): void {
    this.formNavigationFacade.closeDialog();
  }

  public async getSignature(event: File): Promise<void> {
    await this.formMediaFacade.getSignature(event);
  }

  public updateFirma(event: string): void {
    this.formMediaFacade.updateFirma(event);
  }

  public updateImageModel(event: string, fieldName: string): void {
    this.formMediaFacade.updateImageModel(event, fieldName);
  }

  public updateForm(data: Record<string, unknown>, emit: boolean): void {
    this.formPatchFacade.updateForm(data, { emit });
  }

  public forceRehydrateRepeatable(fieldName: string): void {
    const field = this.findFieldByName(fieldName);

    if (field?.input?.type !== InputType.REPEATABLE_GROUP || !this.model) {
      return;
    }

    const bindingControlNames = Array.from(new Set(
      Object.values(field.input.repeatableGroupConfiguration?.bindings ?? {})
        .map((controlName) => String(controlName ?? '').trim())
        .filter((controlName) => controlName !== '')
    ));

    for (const controlName of bindingControlNames) {
      const control = this.model.get(controlName);

      if (control) {
        control.patchValue(control.value, { emitEvent: true });
      }
    }

    this.markForCheck();
  }

  /**
   * Progressive field rendering: ready when list has real options (or all dropdowns finalized).
   * IMPORTANT: if list is empty and finalized, field must still render (empty dropdown, not removed).
   */
  public isFieldReady(field: FormField<T> | string): boolean {
    const resolvedField = typeof field === 'string' ? this.findFieldByName(field) : field;

    return resolvedField ? this.formViewStateFacade.isFieldReady(resolvedField) : false;
  }

  /**
   * Returns true when every requested field has reached a renderable state.
   */
  public areFieldsReady(fieldNames: readonly string[]): boolean {
    return fieldNames.every((fieldName) => this.isFieldReady(fieldName));
  }

  /**
   * Resolves when a field reaches ready, empty or idle state.
   */
  public whenFieldReady(fieldName: string, timeoutMs = DEFAULT_FIELD_READY_TIMEOUT_MS): Promise<boolean> {
    if (this.isFieldReady(fieldName)) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const waiters = this.fieldReadyWaiters.get(fieldName) ?? [];
      let isSettled = false;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      const complete = (isReady: boolean): void => {
        if (isSettled) {
          return;
        }

        isSettled = true;

        if (timeoutId !== null) {
          clearTimeout(timeoutId);
        }

        const currentWaiters = this.fieldReadyWaiters.get(fieldName) ?? [];
        const pendingWaiters = currentWaiters.filter((item) => item !== complete);

        if (pendingWaiters.length > 0) {
          this.fieldReadyWaiters.set(fieldName, pendingWaiters);
        } else {
          this.fieldReadyWaiters.delete(fieldName);
        }

        resolve(isReady);
      };

      waiters.push(complete);
      this.fieldReadyWaiters.set(fieldName, waiters);

      if (timeoutMs > 0) {
        timeoutId = setTimeout(() => complete(this.isFieldReady(fieldName)), timeoutMs);
      }
    });
  }

  /**
   * Resolves when all requested fields have reached a renderable state.
   */
  public async whenFieldsReady(fieldNames: readonly string[], timeoutMs = DEFAULT_FIELD_READY_TIMEOUT_MS): Promise<boolean> {
    const results = await Promise.all(fieldNames.map((fieldName) => this.whenFieldReady(fieldName, timeoutMs)));

    return results.every((isReady) => isReady);
  }


  /**
   * Returns the current runtime state for a top-level field, including dropdown diagnostics when available.
   */
  public getFieldState(fieldName: string): ReactiveFieldRuntimeState<T> | null {
    return this.formViewStateFacade.fieldStates.get(fieldName) ?? null;
  }

  /**
   * Returns dropdown diagnostics without exposing the internal field-state map to host components.
   */
  public getDropdownDiagnostics(fieldName: string): ReactiveFieldRuntimeState<T>['diagnostics'] | null {
    return this.getFieldState(fieldName)?.diagnostics ?? null;
  }

  /**
   * Returns true when a repeatable group has no pending dropdown hydration work.
   */
  public isRepeatableReady(fieldName: string): boolean {
    return this.repeatableReadiness.isReady(fieldName);
  }

  /**
   * Resolves when a repeatable group finishes hydrating its rows and pending dropdown values.
   */
  public whenRepeatableReady(fieldName: string, timeoutMs = DEFAULT_FIELD_READY_TIMEOUT_MS): Promise<boolean> {
    return this.repeatableReadiness.whenReady(fieldName, timeoutMs);
  }

  /**
   * Returns diagnostics for a repeatable group readiness state.
   */
  public getRepeatableState(fieldName: string): RepeatableReadinessState | null {
    return this.repeatableReadiness.getState(fieldName);
  }

  /**
   * Returns pending dropdown diagnostics for a repeatable group without exposing component internals.
   */
  public getRepeatablePendingDropdowns(fieldName: string): readonly RepeatablePendingDropdownDiagnostic[] {
    return this.repeatableReadiness.getPendingDropdowns(fieldName);
  }

  /**
   * Returns an aggregate readiness summary for all registered repeatable groups.
   */
  public getRepeatableReadinessSummary(): RepeatableReadinessSummary {
    return this.repeatableReadiness.getSummary();
  }

  /**
   * Small preview while the field is not ready.
   */
  public getPendingDisplay(field: FormField<T>): string {
    return this.formViewStateFacade.getPendingDisplay(field);
  }

  private findFieldByName(fieldName: string): FormField<T> | null {
    for (const section of this.form?.sections ?? []) {
      const field = section.fields.find((item) => item.name === fieldName);

      if (field) {
        return field;
      }
    }

    return null;
  }

  private resolveReadyWaiters(): void {
    for (const [fieldName, waiters] of this.fieldReadyWaiters.entries()) {
      if (!this.isFieldReady(fieldName)) {
        continue;
      }

      this.fieldReadyWaiters.delete(fieldName);
      waiters.forEach((resolve) => resolve(true));
    }
  }

  private resolveAllReadyWaiters(isReady: boolean): void {
    for (const waiters of this.fieldReadyWaiters.values()) {
      waiters.forEach((resolve) => resolve(isReady));
    }

    this.fieldReadyWaiters.clear();
  }

  private getDropdownSourceValue(fieldName: string, explicitValue: unknown, hasExplicitValue: boolean): unknown {
    return hasExplicitValue ? explicitValue : this.model?.get(fieldName)?.value;
  }

  private getCurrentModelValue(): ReactiveFormValue<T> {
    return this.model.getRawValue() as ReactiveFormValue<T>;
  }

  private refreshViewState(options: { markForCheck?: boolean; render?: boolean } = {}): void {
    this.formViewStateFacade.syncFieldRuntimeStates();
    this.formViewStateFacade.updateLoadingState();
    this.resolveReadyWaiters();

    if (options.render === true) {
      this.formViewStateFacade.refreshRenderedFields();
    }

    if (options.markForCheck === true) {
      this.markForCheck();
    }
  }

  private markForCheck(): void {
    this.cdr?.markForCheck();
  }

  private assignDropdownEngineForm(form: Form<T>): void {
    this.form = form;
  }

  private subscribeToFormChanges(): void {
    this.formEventFacade.subscribeToModelChanges(this.model, this.destroy$, (newValuesRaw) => this.handleModelValueChange(newValuesRaw));
  }

  private async handleModelValueChange(newValuesRaw: unknown): Promise<void> {
    const currentRawValue = this.getCurrentModelValue();
    const changeEvaluation = this.formEventFacade.evaluateValueChange(newValuesRaw, {
      id: this.id,
      previousDataReady: this.previousDataReady,
      currentRawValue,
    });

    if (!changeEvaluation.shouldProcess || changeEvaluation.changedField === null) {
      return;
    }

    this.formEventFacade.publishRawValueChange(
      changeEvaluation.value,
      changeEvaluation.changedField,
      changeEvaluation.changedFields,
      changeEvaluation.origin,
    );

    for (const fieldName of changeEvaluation.changedFields) {
      if (this.formModelFacade.isRepeatableBindingControlName(fieldName)) {
        continue;
      }

      this.formModelFacade.applySelected(
        this.formModelFacade.getFieldByControlName(fieldName),
        changeEvaluation.value[fieldName] as T,
      );

      await this.formModelFacade.executeFieldsLogic(fieldName, changeEvaluation.origin);
    }

    await this.formDropdownFacade.loadDependent(changeEvaluation.changedFields, { origin: changeEvaluation.origin });

    const stableModel = this.getCurrentModelValue();

    this.refreshViewState({ render: true });
    this.formEventFacade.publishStableValueChange(
      stableModel,
      changeEvaluation.changedField,
      changeEvaluation.changedFields,
      changeEvaluation.origin,
    );
    this.formEventFacade.syncPreviousModel(stableModel);
  }


  /**
   * Marks parent-managed dropdown lists as loaded after the container updates external option lists.
   * Use this when a container updates external DropdownData lists through build()/pool().
   */
  public finalizeExternalDropdowns(): void {
    void this.finalizeExternalDropdownsCoalesced('dropdownFinalized');
  }

  private async finalizeExternalDropdownsCoalesced(origin: ReactiveChangeOrigin): Promise<void> {
    if (this.isFinalizingExternalDropdowns) {
      this.hasPendingExternalDropdownFinalization = true;

      return;
    }

    this.isFinalizingExternalDropdowns = true;

    try {
      do {
        this.hasPendingExternalDropdownFinalization = false;
        await this.markExternalDropdownsReady(origin);
      } while (this.hasPendingExternalDropdownFinalization && !this.isDestroyed);
    } finally {
      this.isFinalizingExternalDropdowns = false;
    }
  }

  /**
   * Marks parent-managed dropdown lists as pending again before the parent reloads them.
   */
  public resetExternalDropdowns(): void {
    this.formDropdownFacade.resetExternalDropdowns();
    this.refreshViewState({ markForCheck: true });
  }

  /**
   * Refreshes dropdown mappings after a parent component updates external option lists.
   * Prefer this direct API over component-level ReplaySubject refresh triggers.
   */
  public refreshExternalDropdownMappings(): void {
    this.requestExternalDropdownMappingsRefresh();
  }

  public requestExternalDropdownMappingsRefresh(): void {
    if (this.isDestroyed) {
      return;
    }

    this.hasPendingDropdownMappingsRefresh = true;
    this.scheduleExternalDropdownMappingsRefresh();
  }

  private scheduleExternalDropdownMappingsRefresh(): void {
    if (this.isDropdownMappingsRefreshScheduled || this.isDestroyed) {
      return;
    }

    this.isDropdownMappingsRefreshScheduled = true;
    this.scheduleNextFrame(() => {
      void this.flushExternalDropdownMappingsRefresh().catch((error: unknown) => {
        console.error('[ReactiveFormBuilder] Failed to refresh external dropdown mappings.', error);
      });
    });
  }

  private async flushExternalDropdownMappingsRefresh(): Promise<void> {
    this.isDropdownMappingsRefreshScheduled = false;

    if (this.isDestroyed || !this.hasPendingDropdownMappingsRefresh) {
      return;
    }

    if (this.isRefreshingDropdownMappings) {
      this.scheduleExternalDropdownMappingsRefresh();

      return;
    }

    this.hasPendingDropdownMappingsRefresh = false;
    await this.refreshDropdownMappings();

    if (this.hasPendingDropdownMappingsRefresh) {
      this.scheduleExternalDropdownMappingsRefresh();
    }
  }

  private scheduleNextFrame(callback: () => void): void {
    if (typeof globalThis.requestAnimationFrame === 'function') {
      globalThis.requestAnimationFrame(callback);

      return;
    }

    globalThis.setTimeout(callback, 0);
  }

  private async refreshDropdownMappings(): Promise<void> {
    if (this.isRefreshingDropdownMappings) {
      return;
    }

    const refreshSignature = this.createDropdownRefreshSignature();

    if (refreshSignature === this.lastDropdownRefreshSignature) {
      return;
    }

    this.isRefreshingDropdownMappings = true;
    const scope = this.diagnostics?.start('dropdown-trigger-update', 'reactive-form-builder', this.getDiagnosticsMetadata());

    try {
      await this.formFacade.refreshDropdownMappings();
      this.lastDropdownRefreshSignature = this.createDropdownRefreshSignature();
      scope?.end();
    } catch (error: unknown) {
      scope?.fail(error);
      throw error;
    } finally {
      this.isRefreshingDropdownMappings = false;
    }
  }

  /**
   * Publishes stable business changes performed internally with emitEvent: false.
   */
  private publishStableModelSnapshot(changedField: string, origin: ReactiveChangeOrigin = 'programmatic'): void {
    const currentModel = this.getCurrentModelValue();

    this.formEventFacade.publishStableSnapshot(currentModel, changedField, origin);
  }

  /**
   * Publishes internal mutations made with emitEvent: false through a dedicated lifecycle channel.
   */
  private publishSilentModelSnapshot(changedField: string, origin: ReactiveChangeOrigin = 'programmatic'): void {
    const currentModel = this.getCurrentModelValue();

    this.formEventFacade.publishSilentSnapshot(currentModel, changedField, origin);
  }


  public areExternalDropdownsReady(): boolean {
    return this.formDropdownFacade.areExternalDropdownsReady();
  }

  private markExternalDropdownsReady(origin: ReactiveChangeOrigin): Promise<void> {
    return this.formDropdownFacade.finalizeExternalDropdowns(origin);
  }

  private canMutateModelSafely(): boolean {
    return this.isBootstrapped && !!this.form && !!this.model && this.formModelFacade.hasFormOperations();
  }

  public refreshDeclarativeDropdown(fieldName: string): void {
    if (fieldName.trim() === '') {
      return;
    }

    void this.formDropdownFacade.refresh(fieldName, { origin: 'dropdownRefresh' });
  }

  public requestLazyDropdownFilter(request: { readonly fieldName: string; readonly filter: string }): void {
    const fieldName = request.fieldName.trim();
    const filter = request.filter.trim();

    if (fieldName === '' || filter === '') {
      return;
    }

    this.lazyDropdownFilterRequested.emit({ fieldName, filter });
  }

  public loadLazyDeclarativeDropdown(fieldName: string): void {
    const normalizedFieldName = fieldName.trim();

    if (normalizedFieldName === '') {
      return;
    }

    this.lazyDropdownRequested.emit(normalizedFieldName);
    void this.formDropdownFacade.loadOnDemand(normalizedFieldName, { origin: 'dropdownRefresh' });
  }

  private async applyDropdownResolvedFieldData(fieldName: string, origin: ReactiveChangeOrigin): Promise<void> {
    this.formModelFacade.rebuildFormOperations();
    this.formModelFacade.remapDropdownFieldsPristine();
    await this.formModelFacade.executeFieldsLogic(fieldName, origin);
  }

  private completeDropdownDataCascade(fieldName: string, origin: ReactiveChangeOrigin): void {
    this.refreshViewState({ render: true });

    if (!this.id || this.previousDataReady) {
      this.publishSilentModelSnapshot(fieldName, origin);
    }
  }

  private handleDropdownEngineStateChange(): void {
    this.refreshViewState({ markForCheck: true });
  }

  private createDropdownRefreshSignature(): string {
    return JSON.stringify({
      model: this.toStableComparableValue(this.model ? this.getCurrentModelValue() : {}, 0),
      dropdowns: this.createDropdownDataSignature(),
    });
  }

  private createDropdownDataSignature(): readonly unknown[] {
    const form = this.form;

    if (!form) {
      return [];
    }

    return form.sections
      .flatMap((section) => section.fields)
      .filter((field) => this.isDropdownData(field.input?.data))
      .map((field) => {
        const data = field.input.data as DropdownData<unknown>;

        return {
          name: field.name,
          provenance: data.provenance ?? null,
          valuesCount: data.values.length,
          filteredCount: data.filtered.length,
          values: data.values
            .slice(0, MAX_DROPDOWN_REFRESH_SIGNATURE_OPTIONS)
            .map((option) => this.createDropdownOptionSignature(option)),
        };
      })
      .sort((first, second) => first.name.localeCompare(second.name));
  }

  private createDropdownOptionSignature(option: Value<unknown>): Record<string, unknown> {
    return {
      disabled: option.disabled === true,
      group: option.group ?? null,
      label: option.label ?? '',
      value: this.toStableComparableValue(option.value, 0),
      valueAliases: this.toStableComparableValue(option.valueAliases ?? [], 0),
    };
  }

  private isDropdownData(value: unknown): value is DropdownData<unknown> {
    if (value === null || typeof value !== 'object') {
      return false;
    }

    const dropdownData = value as DropdownData<unknown>;

    return Array.isArray(dropdownData.values) && Array.isArray(dropdownData.filtered);
  }

  private toStableComparableValue(value: unknown, depth: number): unknown {
    if (value === null || value === undefined) {
      return null;
    }

    if (value instanceof Date) {
      return value.toISOString();
    }

    if (Array.isArray(value)) {
      if (depth >= MAX_DROPDOWN_REFRESH_SIGNATURE_DEPTH) {
        return `[array:${value.length}]`;
      }

      return value.map((item) => this.toStableComparableValue(item, depth + 1));
    }

    if (typeof value === 'object') {
      if (depth >= MAX_DROPDOWN_REFRESH_SIGNATURE_DEPTH) {
        return '[object]';
      }

      return Object.entries(value as Record<string, unknown>)
        .filter(([, item]) => item !== undefined)
        .sort(([firstKey], [secondKey]) => firstKey.localeCompare(secondKey))
        .map(([key, item]) => [key, this.toStableComparableValue(item, depth + 1)]);
    }

    return value;
  }

  private fetchPreviousDataById(id: string, onResolved: (previousData: T | null) => void): void {
    if (this.getRequestAdapter?.getById) {
      void this.fetchPreviousDataByIdWithAdapter(id, onResolved);
      return;
    }

    const requestData = this.getRequest.getRequestData?.();
    const endpoint = String(requestData?.endpoint ?? '');

    const params = endpoint.startsWith('generic/')
      ? { p3: id }
      : { id };

    if (this.getRequest.perform) {
      this.getRequest.perform(params as any);
    } else {
      this.getRequest.safePerform(params as any);
    }
    this.getRequest.response((res: unknown) => {
      if (this.getRequest.hasError) {
        return;
      }

      this.resolvePreviousDataResponse(id, res, onResolved);
    });
  }

  private async fetchPreviousDataByIdWithAdapter(id: string, onResolved: (previousData: T | null) => void): Promise<void> {
    const getRequestAdapter = this.getRequestAdapter;

    if (!getRequestAdapter?.getById) {
      return;
    }

    try {
      const response = await executeReactiveFormRequestAdapter(
        getRequestAdapter,
        { operation: 'getById', id },
        createReactiveFormRequestAdapterState(),
      );

      this.resolvePreviousDataResponse(id, response, onResolved);
    } catch (error) {
      console.error('ReactiveFormRequestAdapter getById failed:', error);
    }
  }

  private resolvePreviousDataResponse(id: string, response: unknown, onResolved: (previousData: T | null) => void): void {
    const arr = response as Array<unknown> | undefined;
    const first = Array.isArray(arr) ? arr[0] : response;

    if (first && typeof first === 'object' && (first as { readonly id?: unknown }).id?.toString() !== id?.toString()) {
      return;
    }

    onResolved((first as T) ?? null);
  }

  private async executeBeforeRepeatableHydration(): Promise<void> {
    const previousData = this.previousData;
    const form = this.form;
    const hook = form?.beforeRepeatableHydration;

    if (!hook || previousData === null || !form) {
      return;
    }

    await hook({ previousData, form });
  }

  private getServerUrl(): string {
    return this.serverUrl || this.builderEnvironment.serverUrl || '';
  }

  private getDiagnosticsMetadata(): Record<string, unknown> {
    return {
      formName: String(this.form?.name ?? ''),
      formType: this.form?.type ?? null,
      hasId: Boolean(this.id),
      externalDropdownsReady: this.areExternalDropdownsReady(),
    };
  }
}
